import { useRef, useState } from 'react';

import { extractApiErrorMessage } from 'helpers/api-error-snackbar.helpers';

import useModifierAnalytics from './Modifiers.analytics';
import { namedOptions, toGroupFields, toGroupPayload, visibleModifiers } from './Modifiers.formState';
import { planGroupFieldChanges, planItemLinks, planOptionChanges, planOrderChange } from './Modifiers.savePlan';
import useModifierMutations from './useModifierMutations';

export const SAVE_FAILURE = {
  GENERIC: 'generic',
  RULE: 'rule',
};

const SAVE_BLOCK_MESSAGE = /available modifier\(s\), below the group's required minimum of/;

const classifyFailure = error => {
  if (error?.response?.status !== 400) return SAVE_FAILURE.GENERIC;
  const message = extractApiErrorMessage(error) || '';
  return SAVE_BLOCK_MESSAGE.test(message) ? SAVE_FAILURE.RULE : SAVE_FAILURE.GENERIC;
};

const reconcileSavedGroup = (group, optionPlan, createdModifiers, modifierOrder) => {
  const updatesById = new Map(optionPlan.updated.map(change => [change.modifierId, change.updates]));
  const removedIds = new Set(optionPlan.removed);

  const kept = (group?.modifiers || [])
    .filter(modifier => !removedIds.has(modifier.core?.id))
    .map(modifier => {
      const updates = updatesById.get(modifier.core?.id);
      if (!updates) return modifier;
      const { enabled, soldOut, ...core } = updates;
      return { ...modifier, enabled, soldOut, core: { ...modifier.core, ...core } };
    });

  const modifiers = [...kept, ...createdModifiers.filter(Boolean)];
  if (!modifierOrder) return { ...group, modifiers };

  const position = new Map(modifierOrder.map((id, index) => [id, index]));
  const positionOf = modifier =>
    position.has(modifier.core?.id) ? position.get(modifier.core.id) : modifierOrder.length;
  return { ...group, modifiers: [...modifiers].sort((a, b) => positionOf(a) - positionOf(b)) };
};

const accumulate = (previous, current) => {
  if (!previous) return current;

  return {
    ...current,
    isNew: previous.isNew || current.isNew,
    duplicatedFrom: previous.duplicatedFrom || current.duplicatedFrom,
    groupFieldsChanged: previous.groupFieldsChanged || current.groupFieldsChanged,
    reordered: previous.reordered || current.reordered,
    optionsAdded: (previous.optionsAdded || 0) + (current.optionsAdded || 0),
    optionsUpdated: (previous.optionsUpdated || 0) + (current.optionsUpdated || 0),
    optionsRemoved: (previous.optionsRemoved || 0) + (current.optionsRemoved || 0),
  };
};

export default function useModifierGroupSave({ venueId, group, duplicatedFrom, adoptSavedOption, onSaved }) {
  const [saveFailed, setSaveFailed] = useState(false);
  const [savedGroup, setSavedGroup] = useState(null);
  const savingRef = useRef(false);
  const pendingReportRef = useRef(null);
  const { trackGroupSaved } = useModifierAnalytics({ venueId });

  const {
    createGroup,
    createModifier,
    linkGroupToItems,
    refreshGroups,
    removeModifier,
    unlinkGroupFromItem,
    updateGroup,
    updateModifier,
  } = useModifierMutations({ venueId });

  const workingGroup = savedGroup || group;

  const saveGroup = async formValues => {
    const groupId = workingGroup?.core?.id;
    if (!groupId) {
      const created = await createGroup.mutateAsync(toGroupPayload(formValues));
      setSavedGroup(created);
      // The server returns the options in the order they were sent, which is the order of the
      // named rows; adopting by client id keeps a blank row among them from taking someone's id.
      const sent = namedOptions(formValues);
      visibleModifiers(created).forEach((modifier, position) =>
        adoptSavedOption(sent[position]?.core?.id, modifier.core.id)
      );
      return { groupId: created?.core?.id, isNew: true, duplicatedFrom, optionCount: sent.length };
    }

    const groupFieldsChanged = planGroupFieldChanges(workingGroup, formValues).length > 0;
    await updateGroup.mutateAsync({ groupId, group: toGroupFields(formValues) });
    const optionPlan = planOptionChanges(workingGroup, formValues);
    const serverIdByClientId = new Map();
    const [created] = await Promise.all([
      Promise.all(
        optionPlan.created.map(({ clientId, modifier }) =>
          createModifier.mutateAsync({ groupId, modifier }).then(saved => {
            serverIdByClientId.set(clientId, saved?.core?.id);
            adoptSavedOption(clientId, saved?.core?.id);
            return saved;
          })
        )
      ),
      Promise.all(optionPlan.updated.map(change => updateModifier.mutateAsync(change))),
      Promise.all(optionPlan.removed.map(modifierId => removeModifier.mutateAsync({ modifierId }))),
    ]);

    // One atomic modifier_order write, only after the creates settle: new options need server
    // ids first, and per-option ordinal writes could half-apply a reorder.
    const orderPlan = planOrderChange(workingGroup, formValues);
    const modifierOrder = orderPlan && orderPlan.map(id => serverIdByClientId.get(id) || id);
    if (modifierOrder) await updateGroup.mutateAsync({ groupId, group: { modifierOrder } });

    setSavedGroup(previous => reconcileSavedGroup(previous || workingGroup, optionPlan, created, modifierOrder));

    return {
      groupId,
      isNew: false,
      optionCount: namedOptions(formValues).length,
      groupFieldsChanged,
      optionsAdded: optionPlan.created.length,
      optionsUpdated: optionPlan.updated.length,
      optionsRemoved: optionPlan.removed.length,
      reordered: Boolean(modifierOrder),
    };
  };

  const save = async formValues => {
    // Disabling the button only takes effect on the next render, so a fast double-click gets two
    // submits through and creates the group twice.
    if (savingRef.current) return;
    savingRef.current = true;
    setSaveFailed(false);

    try {
      const saved = accumulate(pendingReportRef.current, await saveGroup(formValues));
      pendingReportRef.current = saved;
      const { groupId } = saved;
      const { linked, unlinked } = planItemLinks(workingGroup, formValues);

      if (linked.length > 0) await linkGroupToItems.mutateAsync({ groupId, itemIds: linked });
      await Promise.all(unlinked.map(itemId => unlinkGroupFromItem.mutateAsync({ groupId, itemId })));

      trackGroupSaved({ ...saved, itemsAdded: linked, itemsRemoved: unlinked });
      pendingReportRef.current = null;
      refreshGroups();
      onSaved(groupId);
      return undefined;
    } catch (error) {
      const failure = classifyFailure(error);
      setSaveFailed(failure === SAVE_FAILURE.GENERIC);
      return failure;
    } finally {
      savingRef.current = false;
    }
  };

  return { save, saveFailed };
}
